import React from 'react';
import PropTypes from 'prop-types';
import tinycolor from 'tinycolor2';
import styled from 'styled-components';

const FlexDiv = styled.div`
  display: flex;
`;

const defaultColors = [
  '#2ecc71', // emerald
  '#3498db', // peter river
  '#8e44ad', // wisteria
  '#e67e22', // carrot
  '#e8220d', // alizarin
  '#1abc9c', // turquoise
  '#2c3e50', // midnight blue
];

const getInitials = (name) => {
  if (!name) {
    name = 'anonymous user';
  }
  const nameList = name.split(' ');
  return nameList.map((nameItem) => nameItem.charAt(0)).join('');
};

const sumChars = (initials) => {
  return initials
    .split('')
    .map((char, index) => initials.charCodeAt(index))
    .reduce((a, b) => a + b, 0);
};

const idealTextColor = (bgColor) => {
  const nThreshold = 105;
  const components = tinycolor(bgColor).toRgb();
  const bgDelta = components.r * 0.299 + components.g * 0.587 + components.b * 0.114;

  return 255 - bgDelta < nThreshold ? '#000000' : '#ffffff';
};

export default class UserAvatar extends React.Component {
  constructor(props) {
    super(props);
    this.state = { src: props.src };
    this.colors = [];

    this.backgroundColor = this.backgroundColor.bind(this);
    this.setColors = this.setColors.bind(this);
    this.setColors();
    if (this.props.id) {
      window.addEventListener('current_profile_photo_changed', (e) => this.onChangeAvatar(e));
      this.onChangeAvatar.bind(this);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.src !== this.props.src) {
      this.setState({ src: this.props.src });
    }
  }

  onChangeAvatar(e) {
    if (e.detail.id === this.props.id) {
      this.setState({ src: e.detail.avatar_url });
    }
  }

  setColors() {
    if (this.props.colors) {
      this.colors.push(this.props.colors);
    } else if (this.props.seedColor) {
      const color = tinycolor(this.props.seedColor);
      const hsl = color.toHsl();
      const { h } = hsl;
      const modulo = 360 / this.props.sampleSize;

      Array(this.props.sampleSize)
        .fill()
        .map((_, i) => {
          this.colors.push(
            tinycolor({
              h: (h + (i + 1) * modulo) % 360,
              s: Math.min(hsl.s * 1.2, 1),
              l: hsl.l,
            }).toHexString(),
          );
        });
    } else {
      this.colors = defaultColors;
    }
  }

  backgroundColor() {
    const colorIndex = sumChars(getInitials(this.props.name)) % this.colors.length;
    return this.colors[colorIndex];
  }

  render() {
    const avatarImageStyle = {
      width: `${this.props.size}px`,
      height: `${this.props.size}px`,
      minWidth: `${this.props.size}px`,
      minHeight: `${this.props.size}px`,
      borderRadius: '50%',
      objectFit: 'initial',
    };

    const avatarInitialStyle = Object.assign({}, avatarImageStyle, this.props.customStyle, {
      lineHeight: `${this.props.size}px`,
      fontSize: `${this.props.size / 2 - 2}px`,
      textAlign: 'center',
      display: 'inline-block',
      color: idealTextColor(this.backgroundColor()),
      backgroundColor: this.backgroundColor(),
    });
    const renderGenerateAvatar = !!this.state.src && (
      <img
        data-purpose="profile_photo"
        src={this.state.src}
        alt={this.props.name}
        style={avatarImageStyle}
      />
    );
    const renderImageAvatar = !this.state.src && (
      <div style={avatarInitialStyle}>{getInitials(this.props.name)}</div>
    );
    return (
      <FlexDiv className="user-avatar" style={this.props.style}>
        {renderGenerateAvatar}
        {renderImageAvatar}
      </FlexDiv>
    );
  }
}

UserAvatar.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.number,
  colors: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  seedColor: PropTypes.string,
  sampleSize: PropTypes.number,
  size: PropTypes.number,
  src: PropTypes.string,
  customStyle: PropTypes.any,
  style: PropTypes.any,
};

UserAvatar.defaultProps = {
  colors: null,
  seedColor: null,
  sampleSize: 6,
  size: 30,
  src: '',
  id: null,
  customStyle: {},
};
